import { CheckCircle2, ShieldCheck } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const Stat = ({ value, label, desc, testId }) => (
  <div
    data-testid={testId}
    className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition-all hover:border-blue-300 hover:shadow-xl hover:shadow-blue-600/10"
  >
    <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-blue-500/10 blur-3xl transition-opacity group-hover:opacity-70" />
    <div className="font-heading bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-5xl font-black tracking-tighter text-transparent md:text-6xl">
      {value}
    </div>
    <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-blue-600">
      {label}
    </div>
    <p className="mt-3 max-w-xs text-sm leading-relaxed text-slate-600">
      {desc}
    </p>
  </div>
);

export const About = () => {
  const { t } = useLang();
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative scroll-mt-[160px] md:scroll-mt-[210px] lg:scroll-mt-[240px] bg-gradient-to-b from-[#d6e6f2] to-[#d8e7f3] py-24 lg:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-blue-600">
              {t.about.overline}
            </div>
            <h2
              data-testid="about-title"
              className="font-heading mt-5 text-3xl font-bold tracking-tight text-slate-900 md:text-4xl lg:text-5xl"
            >
              {t.about.title}
            </h2>
          </div>
          <div className="space-y-6 lg:col-span-7">
            <p className="text-base leading-relaxed text-slate-700 md:text-lg">
              {t.about.body_1}
            </p>
            <ul
              data-testid="about-specialties"
              className="grid grid-cols-1 gap-3 sm:grid-cols-2"
            >
              {t.about.specialties.map((s) => (
                <li
                  key={s.title}
                  className="flex items-start gap-2.5 text-sm text-slate-700 md:text-base"
                >
                  <CheckCircle2
                    className="mt-0.5 h-4 w-4 shrink-0 text-blue-600"
                    strokeWidth={2.4}
                  />
                  <span>
                    <span className="font-semibold text-slate-900">
                      {s.title}
                    </span>
                    {s.desc && (
                      <>
                        <span className="text-slate-500"> — </span>
                        <span className="text-slate-600">{s.desc}</span>
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-base leading-relaxed text-slate-600">
              {t.about.body_2}
            </p>
            <div
              data-testid="about-clientes"
              className="relative overflow-hidden rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 via-[#d8e8f5] to-sky-50 p-6 shadow-sm"
            >
              <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-blue-500/10 blur-3xl" />
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-blue-700">
                <ShieldCheck className="h-4 w-4" strokeWidth={2.4} />
                {t.about.clientes_title}
              </div>
              <p className="mt-3 text-sm leading-relaxed text-slate-700 md:text-base">
                {t.about.clientes_body}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-3">
          <Stat
            testId="stat-team"
            value="50+"
            label={t.about.stat_team}
            desc={t.about.stat_team_desc}
          />
          <Stat
            testId="stat-years"
            value="30+"
            label={t.about.stat_years}
            desc={t.about.stat_years_desc}
          />
          <Stat
            testId="stat-brands"
            value="5"
            label={t.about.stat_brands}
            desc={t.about.stat_brands_desc}
          />
        </div>
      </div>
    </section>
  );
};
